/**
 * DevelopmentProcessSteps Component
 *
 * Displays jurisdiction-specific development process steps for a project.
 * Includes alerts for Jacksonville-specific requirements like plat approval,
 * mobility fees, and master permit requirements.
 */

import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CheckCircle2,
  Circle,
  Clock,
  AlertTriangle,
  FileText,
  Building2,
  Users,
  Info,
  Zap,
  Droplets,
} from 'lucide-react';
import { useDevelopmentProcessSteps, type DevelopmentProcessStep } from '@/hooks/useProject';
import type { DevelopmentProjectType } from '@/types/project';

interface DevelopmentProcessStepsProps {
  projectTypeCode: DevelopmentProjectType;
  jurisdictionId?: number;
  jurisdictionCode?: string;
}

// Jacksonville-specific alert configurations
const JACKSONVILLE_ALERTS = [
  {
    code: 'plat_required',
    title: 'Plat Approval Required',
    description: 'Jacksonville subdivisions require plat approval before building permits. The plat process typically takes 4-6 months and includes Planning Commission review.',
    icon: FileText,
    projectTypes: ['subdivision', 'townhome'],
  },
  {
    code: 'mobility_fee',
    title: 'Mobility Fee Required',
    description: 'Jacksonville assesses mobility fees for new development based on trip generation. Calculate fees early to include in project budget. Fee varies by land use type.',
    icon: Building2,
    projectTypes: ['subdivision', 'townhome', 'apartment', 'mixed_use', 'commercial_complex'],
  },
  {
    code: 'master_permit',
    title: 'Master Permit Option',
    description: 'For developments with 5+ identical units, Jacksonville offers a Master Permit that streamlines the review process. Submit one complete set for review, then pull individual permits referencing the master.',
    icon: Users,
    projectTypes: ['townhome', 'apartment', 'subdivision'],
  },
];

// JEA coordination prompts
const JEA_COORDINATION = {
  title: 'JEA Coordination Required',
  description: 'Contact JEA (Jacksonville Electric Authority) early for water/sewer availability, electric service requirements, and reclaimed water availability. JEA typically requires 30-60 day lead time for development coordination.',
  contacts: [
    { label: 'Development Services', phone: '(904) 665-4636' },
    { label: 'Electric Service', phone: '(904) 665-6000' },
    { label: 'Water/Sewer', phone: '(904) 665-6000' },
  ],
};

export function DevelopmentProcessSteps({
  projectTypeCode,
  jurisdictionId,
  jurisdictionCode,
}: DevelopmentProcessStepsProps) {
  // Fetch process steps
  const { steps, isLoading, error } = useDevelopmentProcessSteps(
    projectTypeCode,
    jurisdictionId
  );

  // Check if Jacksonville jurisdiction
  const isJacksonville = useMemo(() => {
    return jurisdictionCode?.toLowerCase().includes('jacksonville') ||
           jurisdictionCode?.toLowerCase().includes('jax') ||
           jurisdictionCode?.toLowerCase().includes('duval');
  }, [jurisdictionCode]);

  // Filter applicable Jacksonville alerts
  const jacksonvilleAlerts = useMemo(() => {
    if (!isJacksonville) return [];
    return JACKSONVILLE_ALERTS.filter((alert) =>
      alert.projectTypes.includes(projectTypeCode)
    );
  }, [isJacksonville, projectTypeCode]);

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72 mt-2" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  // No steps available
  if (steps.length === 0 && !isJacksonville) {
    return null; // Don't show empty card
  }

  return (
    <div className="space-y-4">
      {/* Jacksonville-specific alerts */}
      {jacksonvilleAlerts.length > 0 && (
        <div className="space-y-3">
          {jacksonvilleAlerts.map((alert) => {
            const Icon = alert.icon;
            return (
              <Alert key={alert.code} className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-900">
                <Icon className="h-4 w-4 text-blue-600" />
                <AlertTitle className="text-blue-800 dark:text-blue-200">{alert.title}</AlertTitle>
                <AlertDescription className="text-blue-700 dark:text-blue-300">
                  {alert.description}
                </AlertDescription>
              </Alert>
            );
          })}
        </div>
      )}

      {/* JEA Coordination for Jacksonville */}
      {isJacksonville && (
        <Alert className="border-yellow-200 bg-yellow-50/50 dark:bg-yellow-950/20 dark:border-yellow-900">
          <Zap className="h-4 w-4 text-yellow-600" />
          <AlertTitle className="text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
            {JEA_COORDINATION.title}
            <Badge variant="outline" className="text-xs">Utility</Badge>
          </AlertTitle>
          <AlertDescription className="text-yellow-700 dark:text-yellow-300">
            <p className="mb-2">{JEA_COORDINATION.description}</p>
            <div className="flex flex-wrap gap-3 mt-2">
              {JEA_COORDINATION.contacts.map((contact) => (
                <span key={contact.label} className="text-sm">
                  <strong>{contact.label}:</strong> {contact.phone}
                </span>
              ))}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Process Steps */}
      {steps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Development Process Steps
            </CardTitle>
            <CardDescription>
              Jurisdiction-specific steps for this project type
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {steps.map((step, index) => (
                <AccordionItem key={step.id} value={step.id}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3 text-left">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-medium">
                        {step.stepNumber}
                      </div>
                      <div>
                        <p className="font-medium">{step.stepName}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {step.required && (
                            <Badge variant="secondary" className="text-xs">Required</Badge>
                          )}
                          {step.typicalDuration && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {step.typicalDuration}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pl-11">
                    <div className="space-y-3">
                      {step.description && (
                        <p className="text-sm text-muted-foreground">{step.description}</p>
                      )}

                      {step.responsibleParty && (
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span><strong>Responsible:</strong> {step.responsibleParty}</span>
                        </div>
                      )}

                      {step.deliverables && step.deliverables.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-1">Deliverables:</p>
                          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                            {step.deliverables.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {step.agencyReviews && step.agencyReviews.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-1">Agency Reviews:</p>
                          <div className="flex flex-wrap gap-2">
                            {step.agencyReviews.map((agency, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {agency}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {step.tips && step.tips.length > 0 && (
                        <div className="bg-muted/50 rounded-lg p-3">
                          <p className="text-sm font-medium mb-1 flex items-center gap-1">
                            <Info className="h-4 w-4" />
                            Tips:
                          </p>
                          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                            {step.tips.map((tip, i) => (
                              <li key={i}>{tip}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
